import clsx from "clsx";

import {
  NO_SCENE_CONTEXT_COLOR,
  NO_SCENE_CONTEXT_ID,
} from "@excalidraw/element";

import {
  actionSetActiveSceneContext,
  actionToggleSceneContextFocus,
  actionToggleSceneContextsPanel,
  getSceneContextDisplayName,
} from "../actions/actionSceneContexts";
import { t } from "../i18n";

import { PlusIcon, sceneContextFocusIcon } from "./icons";

import "./SceneContextsMenu.scss";

import type { ActionManager } from "../actions/manager";
import type { UIAppState } from "../types";

/**
 * One chip per context in the footer; clicking a chip makes that context the
 * active one (turning context mode on). "No context" stops assigning. The
 * focus button next to the chips dims everything outside the active context;
 * the "+" button opens the Contexts panel (add contexts, settings). The chips
 * and the focus button are shown once a registry exists.
 */
export const SceneContextFooterSwitcher = ({
  appState,
  actionManager,
}: {
  appState: UIAppState;
  actionManager: ActionManager;
}) => {
  const {
    sceneContexts,
    activeSceneContextId,
    sceneContextModeEnabled,
    sceneContextFocusEnabled,
    viewModeEnabled,
  } = appState;

  if (viewModeEnabled) {
    return null;
  }

  const hasContexts = sceneContexts.length > 0;

  // the context new elements are assigned to right now
  const assignedId = sceneContextModeEnabled ? activeSceneContextId : null;

  const chips = [
    ...sceneContexts.map((sceneContext) => ({
      id: sceneContext.id,
      color: sceneContext.color,
      isActive: sceneContext.id === assignedId,
    })),
    {
      id: NO_SCENE_CONTEXT_ID,
      color: NO_SCENE_CONTEXT_COLOR,
      isActive:
        !assignedId ||
        !sceneContexts.some((sceneContext) => sceneContext.id === assignedId),
    },
  ];

  return (
    <div className="scene-context-footer">
      {hasContexts && (
        <div
          className="scene-context-switcher"
          role="radiogroup"
          aria-label={t("sceneContexts.switcherLabel")}
          data-testid="scene-context-switcher"
        >
          {chips.map((chip) => {
            const name = getSceneContextDisplayName(chip.id, sceneContexts);
            return (
              <button
                key={chip.id}
                type="button"
                role="radio"
                aria-checked={chip.isActive}
                className={clsx("scene-context-chip", {
                  "scene-context-chip--active": chip.isActive,
                })}
                data-testid={`scene-context-chip-${chip.id}`}
                title={name}
                onClick={() =>
                  actionManager.executeAction(
                    actionSetActiveSceneContext,
                    "ui",
                    {
                      id: chip.id,
                      enableMode: true,
                    },
                  )
                }
              >
                <span
                  className="scene-contexts__swatch"
                  style={{ backgroundColor: chip.color }}
                  aria-hidden="true"
                />
                <span className="scene-context-chip__label">{name}</span>
              </button>
            );
          })}
        </div>
      )}
      {hasContexts && (
        <button
          type="button"
          className={clsx("scene-context-chip scene-context-icon-button", {
            "scene-context-chip--active": sceneContextFocusEnabled,
          })}
          data-testid="scene-context-focus"
          aria-pressed={sceneContextFocusEnabled}
          aria-label={t("sceneContexts.toggleFocus")}
          title={`${t("sceneContexts.toggleFocus")} — ${t(
            "sceneContexts.focusHint",
          )}`}
          onClick={() =>
            actionManager.executeAction(actionToggleSceneContextFocus, "ui")
          }
        >
          {sceneContextFocusIcon}
        </button>
      )}
      <button
        type="button"
        className="scene-context-chip scene-context-icon-button"
        data-testid="scene-context-open-panel"
        aria-label={t("sceneContexts.openPanelHint")}
        title={t("sceneContexts.openPanelHint")}
        onClick={() =>
          actionManager.executeAction(actionToggleSceneContextsPanel, "ui")
        }
      >
        {PlusIcon}
      </button>
    </div>
  );
};
